/**
 * 军队系统状态管理
 */

import { create } from 'zustand'
import type {
  MilitaryState,
  MilitaryFacility,
  MilitaryOrder,
  Unit,
  UnitType,
  ArmyStatus,
  BattleStatus
} from '../types/military'

// 初始状态
const initialState: MilitaryState = {
  armies: new Map(),
  battles: new Map(),
  facilities: new Map(),
  technologies: new Map(),
  recruitmentPlans: new Map(),
  orders: new Map(),
  unitTemplates: new Map(),
  statistics: {
    totalArmies: 0,
    totalStrength: 0,
    totalBattles: 0,
    totalVictories: 0,
    totalDefeats: 0,
    totalFacilities: 0,
    researchedTechnologies: 0
  }
}

// 预设兵种模板
const unitTemplates: Record<UnitType, Unit> = {
  heavy_infantry: {
    id: 'unit_heavy_infantry',
    type: 'heavy_infantry',
    name: '重步兵',
    count: 0,
    attack: 80,
    defense: 90,
    mobility: 40,
    range: 1,
    morale: 70,
    experience: 0,
    training: 50,
    equipment: {
      weapons: ['长矛', '盾牌', '剑'],
      armor: ['铁甲', '头盔'],
      supplies: ['粮食', '医疗用品']
    },
    upkeep: {
      food: 2,
      money: 5
    }
  },
  light_infantry: {
    id: 'unit_light_infantry',
    type: 'light_infantry',
    name: '轻步兵',
    count: 0,
    attack: 60,
    defense: 50,
    mobility: 70,
    range: 1,
    morale: 60,
    experience: 0,
    training: 40,
    equipment: {
      weapons: ['短剑', '标枪'],
      armor: ['皮甲'],
      supplies: ['粮食']
    },
    upkeep: {
      food: 1,
      money: 2
    }
  },
  heavy_cavalry: {
    id: 'unit_heavy_cavalry',
    type: 'heavy_cavalry',
    name: '重骑兵',
    count: 0,
    attack: 90,
    defense: 80,
    mobility: 80,
    range: 1,
    morale: 80,
    experience: 0,
    training: 70,
    equipment: {
      weapons: ['长枪', '剑'],
      armor: ['铁甲', '头盔', '马铠'],
      supplies: ['粮食', '草料']
    },
    upkeep: {
      food: 3,
      money: 8
    }
  },
  light_cavalry: {
    id: 'unit_light_cavalry',
    type: 'light_cavalry',
    name: '轻骑兵',
    count: 0,
    attack: 70,
    defense: 50,
    mobility: 90,
    range: 1,
    morale: 70,
    experience: 0,
    training: 60,
    equipment: {
      weapons: ['弓箭', '短剑'],
      armor: ['皮甲'],
      supplies: ['粮食', '草料']
    },
    upkeep: {
      food: 2,
      money: 5
    }
  },
  archers: {
    id: 'unit_archers',
    type: 'archers',
    name: '弓箭手',
    count: 0,
    attack: 70,
    defense: 30,
    mobility: 50,
    range: 5,
    morale: 50,
    experience: 0,
    training: 60,
    equipment: {
      weapons: ['长弓', '箭袋'],
      armor: ['皮甲'],
      supplies: ['粮食', '箭支']
    },
    upkeep: {
      food: 1,
      money: 3
    }
  },
  crossbowmen: {
    id: 'unit_crossbowmen',
    type: 'crossbowmen',
    name: '弩手',
    count: 0,
    attack: 75,
    defense: 35,
    mobility: 45,
    range: 6,
    morale: 55,
    experience: 0,
    training: 65,
    equipment: {
      weapons: ['弩', '箭袋'],
      armor: ['皮甲'],
      supplies: ['粮食', '箭支']
    },
    upkeep: {
      food: 1,
      money: 4
    }
  },
  siege_weapons: {
    id: 'unit_siege_weapons',
    type: 'siege_weapons',
    name: '攻城器械',
    count: 0,
    attack: 95,
    defense: 20,
    mobility: 10,
    range: 10,
    morale: 40,
    experience: 0,
    training: 80,
    equipment: {
      weapons: ['投石机', '攻城锤'],
      armor: ['木盾'],
      supplies: ['粮食', '石材']
    },
    upkeep: {
      food: 2,
      money: 10
    }
  },
  navy: {
    id: 'unit_navy',
    type: 'navy',
    name: '水军',
    count: 0,
    attack: 70,
    defense: 60,
    mobility: 50,
    range: 4,
    morale: 60,
    experience: 0,
    training: 70,
    equipment: {
      weapons: ['船弩', '火攻器具'],
      armor: ['船甲'],
      supplies: ['粮食', '箭支']
    },
    upkeep: {
      food: 2,
      money: 6
    }
  },
  chariots: {
    id: 'unit_chariots',
    type: 'chariots',
    name: '战车',
    count: 0,
    attack: 85,
    defense: 70,
    mobility: 60,
    range: 3,
    morale: 75,
    experience: 0,
    training: 80,
    equipment: {
      weapons: ['长矛', '弓箭'],
      armor: ['战车护甲'],
      supplies: ['粮食', '草料', '箭支']
    },
    upkeep: {
      food: 4,
      money: 10
    }
  }
}

// 初始化兵种模板
function initializeUnitTemplates() {
  const templates = new Map<UnitType, Unit>()
  Object.entries(unitTemplates).forEach(function(entry) {
    const type = entry[0]
    const template = entry[1]
    templates.set(type as UnitType, template)
  })
  return templates
}

initialState.unitTemplates = initializeUnitTemplates()

interface MilitaryStore extends MilitaryState {
  // 军队管理
  createArmy: (name: string, factionId: string, commanderId: string, location: string) => string
  addUnitToArmy: (armyId: string, unitType: UnitType, count: number) => void
  removeUnitFromArmy: (armyId: string, unitType: UnitType) => void
  updateArmyStatus: (armyId: string, status: ArmyStatus) => void
  moveArmy: (armyId: string, destination: string) => void
  
  // 战斗管理
  startBattle: (name: string, location: string, attackers: string[], defenders: string[]) => string
  updateBattleStatus: (battleId: string, status: BattleStatus) => void
  
  // 军事设施管理
  buildFacility: (type: MilitaryFacility['type'], name: string, cityId: string, factionId: string) => string
  upgradeFacility: (facilityId: string) => void
  
  // 军事科技管理
  researchTechnology: (technologyId: string) => void
  
  // 征兵管理
  createRecruitmentPlan: (factionId: string, cityId: string, units: { type: UnitType; count: number }[]) => string
  
  // 军事指令
  issueOrder: (type: MilitaryOrder['type'], armyId: string, target: string) => string
  
  // 统计
  updateStatistics: () => void
}

export const useMilitaryStore = create<MilitaryStore>((set, get) => ({
  ...initialState,
  
  // 军队管理
  createArmy: function(name, factionId, commanderId, location) {
    const id = 'army_' + Date.now()
    const now = { year: 1046, month: 1, day: 1 }
    
    set(function(state) {
      const newArmies = new Map(state.armies)
      newArmies.set(id, {
        id: id,
        name: name,
        factionId: factionId,
        commanderId: commanderId,
        status: 'stationed',
        currentLocation: location,
        units: [],
        totalStrength: 0,
        supplies: {
          food: 1000,
          ammunition: 500,
          medical: 200
        },
        morale: 80,
        fatigue: 0,
        formation: 'square',
        history: {
          battles: [],
          victories: 0,
          defeats: 0
        },
        createdAt: now,
        lastUpdatedAt: now
      })
      
      return {
        armies: newArmies,
        statistics: {
          ...state.statistics,
          totalArmies: state.statistics.totalArmies + 1
        }
      }
    })
    
    return id
  },
  
  addUnitToArmy: function(armyId, unitType, count) {
    set(function(state) {
      const newArmies = new Map(state.armies)
      const army = newArmies.get(armyId)
      const unitTemplate = state.unitTemplates.get(unitType)
      
      if (army && unitTemplate) {
        const existingUnit = army.units.find(function(u) {
          return u.type === unitType
        })
        let newUnits
        
        if (existingUnit) {
          newUnits = army.units.map(function(u) {
            if (u.type === unitType) {
              return {
                ...u,
                count: u.count + count
              }
            } else {
              return u
            }
          })
        } else {
          const newUnit = Object.assign({}, unitTemplate)
          newUnit.id = 'unit_' + Date.now()
          newUnit.count = count
          newUnits = army.units.concat(newUnit)
        }
        
        const totalStrength = newUnits.reduce(function(sum, unit) {
          return sum + unit.count
        }, 0)
        
        newArmies.set(armyId, {
          ...army,
          units: newUnits,
          totalStrength: totalStrength,
          lastUpdatedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return {
          armies: newArmies,
          statistics: {
            ...state.statistics,
            totalStrength: state.statistics.totalStrength + count
          }
        }
      }
      
      return state
    })
  },
  
  removeUnitFromArmy: function(armyId, unitType) {
    set(function(state) {
      const newArmies = new Map(state.armies)
      const army = newArmies.get(armyId)
      
      if (army) {
        const unitToRemove = army.units.find(function(u) {
          return u.type === unitType
        })
        if (unitToRemove) {
          const newUnits = army.units.filter(function(u) {
            return u.type !== unitType
          })
          const totalStrength = newUnits.reduce(function(sum, unit) {
            return sum + unit.count
          }, 0)
          
          newArmies.set(armyId, {
            ...army,
            units: newUnits,
            totalStrength: totalStrength,
            lastUpdatedAt: { year: 1046, month: 1, day: 1 }
          })
          
          return {
            armies: newArmies,
            statistics: {
              ...state.statistics,
              totalStrength: Math.max(0, state.statistics.totalStrength - unitToRemove.count)
            }
          }
        }
      }
      
      return state
    })
  },
  
  updateArmyStatus: function(armyId, status) {
    set(function(state) {
      const newArmies = new Map(state.armies)
      const army = newArmies.get(armyId)
      
      if (army) {
        newArmies.set(armyId, {
          ...army,
          status: status,
          lastUpdatedAt: { year: 1046, month: 1, day: 1 }
        })
      }
      
      return { armies: newArmies }
    })
  },
  
  moveArmy: function(armyId, destination) {
    set(function(state) {
      const newArmies = new Map(state.armies)
      const army = newArmies.get(armyId)
      
      if (army) {
        newArmies.set(armyId, {
          ...army,
          status: 'marching',
          destination: destination,
          lastUpdatedAt: { year: 1046, month: 1, day: 1 }
        })
      }
      
      return { armies: newArmies }
    })
  },
  
  // 战斗管理
  startBattle: function(name, location, attackers, defenders) {
    const id = 'battle_' + Date.now()
    const now = { year: 1046, month: 1, day: 1 }
    
    set(function(state) {
      const newBattles = new Map(state.battles)
      newBattles.set(id, {
        id: id,
        name: name,
        location: location,
        attackers: attackers.map(function(armyId) {
          const army = state.armies.get(armyId)
          return {
            armyId: armyId,
            factionId: army ? army.factionId : '',
            commanderId: army ? army.commanderId : '',
            strength: army ? army.totalStrength : 0
          }
        }),
        defenders: defenders.map(function(armyId) {
          const army = state.armies.get(armyId)
          return {
            armyId: armyId,
            factionId: army ? army.factionId : '',
            commanderId: army ? army.commanderId : '',
            strength: army ? army.totalStrength : 0
          }
        }),
        status: 'preparing',
        startedAt: now,
        duration: 0,
        result: {
          casualties: {}
        },
        phases: []
      })
      
      // 更新参与战斗的军队状态
      const newArmies = new Map(state.armies)
      attackers.concat(defenders).forEach(function(armyId) {
        const army = newArmies.get(armyId)
        if (army) {
          newArmies.set(armyId, {
            ...army,
            status: 'battling',
            lastUpdatedAt: now
          })
        }
      })
      
      return {
        battles: newBattles,
        armies: newArmies,
        statistics: {
          ...state.statistics,
          totalBattles: state.statistics.totalBattles + 1
        }
      }
    })
    
    return id
  },
  
  updateBattleStatus: function(battleId, status) {
    set(function(state) {
      const newBattles = new Map(state.battles)
      const battle = newBattles.get(battleId)
      
      if (battle) {
        const now = { year: 1046, month: 1, day: 1 }
        newBattles.set(battleId, {
          ...battle,
          status: status,
          endedAt: now,
          duration: 24 // 假设战斗持续24小时
        })
        
        // 更新军队状态
        const newArmies = new Map(state.armies)
        battle.attackers.concat(battle.defenders).forEach(function(participant) {
          const army = newArmies.get(participant.armyId)
          if (army) {
            newArmies.set(participant.armyId, {
              ...army,
              status: 'stationed',
              lastUpdatedAt: now
            })
          }
        })
        
        // 更新统计
        let totalVictories = state.statistics.totalVictories
        let totalDefeats = state.statistics.totalDefeats
        
        if (status === 'victory') {
          totalVictories++
        } else if (status === 'defeat') {
          totalDefeats++
        }
        
        return {
          battles: newBattles,
          armies: newArmies,
          statistics: {
            ...state.statistics,
            totalVictories: totalVictories,
            totalDefeats: totalDefeats
          }
        }
      }
      
      return state
    })
  },
  
  // 军事设施管理
  buildFacility: function(type, name, cityId, factionId) {
    const id = 'facility_' + Date.now()
    const now = { year: 1046, month: 1, day: 1 }
    
    set(function(state) {
      const newFacilities = new Map(state.facilities)
      newFacilities.set(id, {
        id: id,
        type: type,
        name: name,
        cityId: cityId,
        factionId: factionId,
        level: 1,
        effects: {
          trainingSpeed: 10,
          unitQuality: 5,
          recruitmentSpeed: 10,
          equipmentProduction: 5
        },
        status: 'operating',
        builtAt: now,
        upkeep: {
          money: 100,
          workers: 50
        }
      })
      
      return {
        facilities: newFacilities,
        statistics: {
          ...state.statistics,
          totalFacilities: state.statistics.totalFacilities + 1
        }
      }
    })
    
    return id
  },
  
  upgradeFacility: function(facilityId) {
    set(function(state) {
      const newFacilities = new Map(state.facilities)
      const facility = newFacilities.get(facilityId)
      
      if (facility && facility.level < 5) {
        newFacilities.set(facilityId, {
          ...facility,
          level: facility.level + 1,
          effects: {
            trainingSpeed: facility.effects.trainingSpeed + 5,
            unitQuality: facility.effects.unitQuality + 3,
            recruitmentSpeed: facility.effects.recruitmentSpeed + 5,
            equipmentProduction: facility.effects.equipmentProduction + 3
          },
          lastUpgradedAt: { year: 1046, month: 1, day: 1 }
        })
      }
      
      return { facilities: newFacilities }
    })
  },
  
  // 军事科技管理
  researchTechnology: function(technologyId) {
    set(function(state) {
      const newTechnologies = new Map(state.technologies)
      const technology = newTechnologies.get(technologyId)
      
      if (technology && technology.status === 'unresearched') {
        newTechnologies.set(technologyId, {
          ...technology,
          status: 'researched',
          researchedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return {
          technologies: newTechnologies,
          statistics: {
            ...state.statistics,
            researchedTechnologies: state.statistics.researchedTechnologies + 1
          }
        }
      }
      
      return state
    })
  },
  
  // 征兵管理
  createRecruitmentPlan: function(factionId, cityId, units) {
    const id = 'recruitment_' + Date.now()
    const now = { year: 1046, month: 1, day: 1 }
    
    set(function(state) {
      const newPlans = new Map(state.recruitmentPlans)
      newPlans.set(id, {
        id: id,
        factionId: factionId,
        cityId: cityId,
        units: units.map(function(unit) {
          return {
            type: unit.type,
            count: unit.count,
            trainingTime: 30 // 假设训练时间30天
          }
        }),
        costs: {
          money: units.reduce(function(sum, unit) {
            return sum + unit.count * 10
          }, 0),
          food: units.reduce(function(sum, unit) {
            return sum + unit.count * 5
          }, 0),
          resources: {
            metal: units.reduce(function(sum, unit) {
              return sum + unit.count * 2
            }, 0),
            wood: units.reduce(function(sum, unit) {
              return sum + unit.count
            }, 0)
          }
        },
        status: 'planning',
        createdAt: now,
        estimatedCompletion: { year: 1046, month: 2, day: 1 }
      })
      
      return { recruitmentPlans: newPlans }
    })
    
    return id
  },
  
  // 军事指令
  issueOrder: function(type, armyId, target) {
    const id = 'order_' + Date.now()
    const now = { year: 1046, month: 1, day: 1 }
    
    set(function(state) {
      const newOrders = new Map(state.orders)
      newOrders.set(id, {
        id: id,
        type: type,
        armyId: armyId,
        target: target,
        details: {},
        status: 'issued',
        issuedAt: now
      })
      
      return { orders: newOrders }
    })
    
    return id
  },
  
  // 统计
  updateStatistics: function() {
    const state = get()
    let totalStrength = 0
    
    Array.from(state.armies.values()).forEach(function(army) {
      totalStrength += army.totalStrength
    })
    
    set({
      statistics: {
        ...state.statistics,
        totalStrength: totalStrength
      }
    })
  }
}))
